import { Component, DestroyRef, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { TranslocoDirective } from '@jsverse/transloco';
import { LayoutService } from '../../../../../../../../@vex/services/layout.service';
import { ReallocateTarget, TenantUnitReference, TenantUnitReferenceService } from '@eadsysteme/billingclient-v1';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '../../../../../../../services/session.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect } from '@angular/material/select';
import { SnackbarService } from '../../../../../../../services/snackbar.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

@Component({
	selector: 'ara-reallocate-instance-dialog',
	imports: [
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatDialogTitle,
		TranslocoDirective,
		MatButton,
		MatIcon,
		MatDialogContent,
		MatDialogActions,
		MatSelect,
		MatOption,
		FormsModule,
		CdkDrag,
		CdkDragHandle,
	],
	templateUrl: './reallocate-instance-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './reallocate-instance-dialog.component.scss',
})
export class ReallocateInstanceDialogComponent {
	protected layoutService = inject(LayoutService);
	dialogRef = inject<MatDialogRef<ReallocateInstanceDialogComponent>>(MatDialogRef);
	private sessionService = inject(SessionService);
	private snackBar = inject(SnackbarService);
	private tenantUnitReferenceService = inject(TenantUnitReferenceService);

	private destroyRef: DestroyRef = inject(DestroyRef);
	protected instanceToMigrate: WritableSignal<TenantUnitReference> = signal({ exportId: '' });
	protected targetInstance: FormControl<TenantUnitReference> = new FormControl<TenantUnitReference>({ value: { exportId: '' }, disabled: false });
	protected instancesInSync: TenantUnitReference[] = [];
	private billingId: string;

	constructor() {
		const billing = inject(MAT_DIALOG_DATA);

		this.billingId = billing.billingId;
		this.instanceToMigrate.set(billing.instanceToMigrate);

		this.instancesInSync = [];
		this.tenantUnitReferenceService
			.readAllTenantUnitReference(this.sessionService.effectiveArea, this.billingId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (value) => {
					this.instancesInSync = value.filter(
						(currentInstance) => currentInstance.inSync && currentInstance.exportId !== billing.instanceToMigrate.exportId
					);
				},
				error: () => {
					this.snackBar.error('errors.loadingBillingInstances');
					this.targetInstance.setValue(null);
				},
			});
	}

	close(toSave: boolean) {
		if (!toSave) {
			this.dialogRef.close();
			return;
		}
		const target: ReallocateTarget = { targetExportId: this.targetInstance.value.exportId };
		this.dialogRef.close({ sourceExportId: this.instanceToMigrate().exportId, target });
	}
}
